/**
 * Visual workflow builder. The draft autosaves with optimistic concurrency;
 * publishing snapshots it into an immutable version that new runs use.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Group, Panel, Separator, useDefaultLayout } from 'react-resizable-panels';
import { AlertTriangle, ArrowLeft, Check, KeyRound, Loader2, Play, Power, Rocket, Search, Trash2 } from 'lucide-react';
import { DEFAULT_SCHEDULE_TIMEZONE, NODE_TYPES, runSubject, type AutomationGraph, type AutomationNode, type GraphProblem, type NodeType } from '@prism/contracts';
import { PortalPaletteItems, portalStepDefaults } from './portal-steps';
// 1.6.0 (docs/dev/mcp-1.6.0-build.md): site services (W) and AI description (A).
import { siteServiceStepDefaults } from './site-service-step';
import { aiStepDefaults } from './ai-steps';
import { api, ApiError, errorMessage } from '../../api/client';
import { useAutomation, useMe, useRoles, useSchema, useSequences, type AutomationDetail, type LibraryResult } from '../../api/hooks';
import { Modal } from '../../components/Modal';
import { PreflightProblems, RightsCheckBlock, preflightBlocks, preflightRestorable, usePreflight, useRightsCheck } from '../../components/Preflight';
import { toast } from '../../components/Toasts';
import { relative } from '../../lib/format';
import { layoutStorage } from '../../lib/prefs';
import { defaultApprovalRole } from '../../lib/role-permissions';
import { GraphCanvas, NODE_W, nodeIcon } from './GraphCanvas';
import { NodeInspector } from './NodeInspector';
// 1.9.3: several steps at once.
import { primaryOf, removable, removeNodes } from './graphSelection';
import { RunList } from './Workflows';
// 1.6.2: Who can run it (RunAccess.tsx).
import { RunAccessButton } from './RunAccess';
import { queryKeys } from '../../api/queryKeys';

const NONE = '00000000-0000-4000-8000-000000000000';
/** 1.9.3: a pane of the resizable group: its content (the canvas, the inspector) scrolls itself. */
const PANE = { display: 'flex', overflow: 'hidden' } as const;
const PALETTE: NodeType[] = ['condition', 'delay', 'repeat_until', 'validate_metadata', 'update_metadata', 'map_metadata', 'inspect_media', 'generate_derivatives', 'transcode', 'external_transcode', 'run_qc', 'detect_shots', 'copy_to_storage', 'delete', 'export_asset', 'export_sequence', 'export_edl', 'request_approval', 'create_task', 'wait_until_date', 'live_source', 'call_webhook',
  // 1.6.0: site services (W), and For each and Update segment (A).
  'call_site_service', 'for_each_shot', 'for_each_segment', 'update_segment'];

function defaults(type: NodeType, firstField: string | undefined, approvalRole: string, firstDateField = 'f_shoot_date'): Record<string, unknown> {
  const dateRule = { fieldId: firstDateField, offsetDays: 0, time: '09:00', useFieldTime: false, timezone: DEFAULT_SCHEDULE_TIMEZONE };
  switch (type) {
    // 1.0.0-rc.10: the date steps.
    case 'wait_until_date': return { ...dateRule, ifMissing: 'continue' };
    case 'create_task': return { assignee: { type: 'role', key: approvalRole }, title: 'Follow up on {{asset.title}}', details: '', due: { type: 'now', days: 1, hours: 0 } };
    case 'condition': return { all: [{ path: 'asset.mediaKind', op: 'equals', value: 'video' }] };
    case 'validate_metadata': return { required: [firstField ?? 'title'] };
    case 'update_metadata': return { values: {} };
    case 'delay': return { seconds: 300 };
    case 'repeat_until': return { everySeconds: 60, maxAttempts: 30, check: { type: 'asset' }, all: [{ path: 'asset.status', op: 'equals', value: 'ready' }] };
    case 'map_metadata': return { mappings: [{ field: firstField ?? '', from: '{{trigger.payload.title}}', transform: 'none', lookup: {}, fallback: '', mode: 'overwrite' }] };
    // 1.2.0: the master read (the primary) and the result (a rendition), as the schema's defaults.
    case 'transcode': return { preset: 'h264-720p', source: { type: 'primary' }, saveAs: { type: 'rendition' } };
    case 'external_transcode': return { profileId: '', labels: {}, source: { type: 'primary' }, saveAs: { type: 'rendition' } };
    case 'run_qc': return { source: 'original', master: { type: 'primary' } };
    case 'copy_to_storage': return { locationId: NONE, representation: 'original', master: { type: 'primary' } };
    case 'delete': return { target: 'copy', representation: 'original', onlyCopy: 'fail', master: { type: 'primary' } };
    // Export asset (1.2.0): Programme, the format closest to the preset.
    case 'export_asset': return {
      preset: 'h264-1080p', quality: 'original', master: { type: 'variant', variantId: null, format: null }, range: 'full',
      subtitles: { mode: 'none', language: null }, restore: false,
    };
    case 'export_sequence': return { preset: 'h264-1080p', quality: 'original' };
    case 'export_edl': return { title: '{{sequence.name}}' };
    case 'request_approval': return { role: approvalRole, instructions: 'Please review {{asset.title}}.' };
    case 'live_source': return { sourceId: NONE, action: 'start' };
    case 'call_webhook': return { endpointId: NONE, payload: { assetId: '{{asset.id}}', title: '{{asset.title}}' } };
    // 1.3.1 (Q4, portal-steps.tsx).
    case 'assign_for_delivery': return portalStepDefaults();
    // 1.6.0 (site-service-step.tsx, W; ai-steps.tsx, A).
    case 'call_site_service': return siteServiceStepDefaults();
    case 'for_each_shot':
    case 'for_each_segment':
    case 'update_segment': return aiStepDefaults(type);
    default: return {};
  }
}

export function BuilderPage() {
  const { id } = useParams();
  const q = useAutomation(id);
  if (q.isError) return <div className="empty"><div className="empty-inner"><h2>Workflow not found</h2><Link to="/workflows" className="btn">Back to workflows</Link></div></div>;
  if (!q.data) return <div className="empty"><Loader2 className="spin" color="var(--accent)" /></div>;
  return <Builder key={q.data.id} initial={q.data} />;
}

type SaveState = 'saved' | 'pending' | 'saving' | 'conflict' | 'error';

function Builder({ initial }: { initial: AutomationDetail }) {
  const me = useMe().data!;
  const canManage = me.permissions.includes('automation.manage');
  const schemaFields = useSchema().data?.definition.fields;
  const firstField = schemaFields?.[0]?.id;
  const firstDateField = schemaFields?.find((f) => f.type === 'date' || f.type === 'datetime')?.id;
  const roles = useRoles().data?.items ?? [];
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [graph, setGraph] = useState<AutomationGraph>(initial.draft);
  const [name, setName] = useState(initial.name);
  const [description, setDescription] = useState(initial.description);
  const [maxRuns, setMaxRuns] = useState(initial.maxConcurrentRuns);
  const [problems, setProblems] = useState<GraphProblem[]>(initial.problems);
  // 1.9.3: the selected steps, the last one clicked being the primary.
  const [selected, setSelected] = useState<string[]>([]);
  const primary = primaryOf(selected);
  // Deleting more than one step asks first (the flow editor has no undo).
  const [confirmDelete, setConfirmDelete] = useState<string[] | null>(null);
  const layout = useDefaultLayout({ id: 'prism-flow-builder', storage: layoutStorage });
  const [save, setSave] = useState<SaveState>('saved');
  const [draftChanged, setDraftChanged] = useState(initial.draftChanged);
  const [modal, setModal] = useState<null | 'publish' | 'run' | 'token' | 'delete'>(null);
  const revision = useRef(initial.draftRevision);
  const timer = useRef<number | undefined>(undefined);
  const latest = useRef({ graph, name, description, maxRuns });
  latest.current = { graph, name, description, maxRuns };
  const detail = useAutomation(initial.id).data ?? initial;

  const inflight = useRef<Promise<boolean> | null>(null);
  const flush = useCallback(async (): Promise<boolean> => {
    // Saves are serialized so each one carries the revision the previous returned.
    while (inflight.current) await inflight.current;
    const p = save_();
    inflight.current = p;
    try { return await p; } finally { inflight.current = null; }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial.id]);

  const save_ = async (): Promise<boolean> => {
    window.clearTimeout(timer.current);
    timer.current = undefined;
    setSave('saving');
    const { graph: g, name: n, description: d, maxRuns: m } = latest.current;
    try {
      const r = await api<{ draftRevision: number; problems: GraphProblem[] }>(`/api/v1/automations/${initial.id}/draft`, {
        method: 'PUT', json: { baseRevision: revision.current, graph: g, name: n.trim() || 'Untitled workflow', description: d, maxConcurrentRuns: m },
      });
      revision.current = r.draftRevision;
      setProblems(r.problems);
      setDraftChanged(true);
      setSave(timer.current ? 'pending' : 'saved');
      void qc.invalidateQueries({ queryKey: queryKeys.automations() });
      return true;
    } catch (err) {
      const e = err as ApiError;
      setSave(e.status === 409 ? 'conflict' : 'error');
      toast(e.status === 409 ? 'Someone else changed this workflow. Reload to see their version.' : e.message, 'error');
      return false;
    }
  };

  const schedule = () => {
    setSave('pending');
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => void flush(), 700);
  };
  useEffect(() => () => window.clearTimeout(timer.current), []);
  useEffect(() => {
    const warn = (e: BeforeUnloadEvent) => { if (timer.current) e.preventDefault(); };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, []);

  const change = (g: AutomationGraph) => { setGraph(g); latest.current.graph = g; schedule(); };

  function addNode(type: NodeType, fields?: (id: string) => Record<string, unknown>) {
    const base = graph.nodes.find((n) => n.id === primary) ?? graph.nodes.reduce((a, b) => (b.position.x > a.position.x ? b : a));
    let i = 1;
    while (graph.nodes.some((n) => n.id === `${type}_${i}`)) i++;
    const id = `${type}_${i}`;
    let y = base.position.y;
    while (graph.nodes.some((n) => Math.abs(n.position.x - (base.position.x + NODE_W + 60)) < NODE_W && Math.abs(n.position.y - y) < 80)) y += 100;
    const node = { id, type, position: { x: base.position.x + NODE_W + 60, y }, ...defaults(type, firstField, defaultApprovalRole(roles.map((r) => r.key)), firstDateField), ...(fields ? fields(id) : {}) } as AutomationNode;
    const port = NODE_TYPES[base.type].ports.find((p) => !graph.edges.some((e) => e.from === base.id && e.port === p));
    change({ ...graph, nodes: [...graph.nodes, node], edges: port ? [...graph.edges, { from: base.id, port, to: id }] : graph.edges });
    setSelected([id]);
  }

  /** Delete or Backspace, or Delete steps: one goes at once, several ask first. Never the trigger. */
  function deleteSteps(ids: string[]) {
    const gone = removable(graph, ids);
    if (!gone.length) return;
    if (gone.length > 1) { setConfirmDelete(gone); return; }
    removeSteps(gone);
  }
  function removeSteps(ids: string[]) {
    change(removeNodes(latest.current.graph, ids));
    setSelected((sel) => sel.filter((id) => !ids.includes(id)));
    setConfirmDelete(null);
  }

  async function setEnabled(enabled: boolean) {
    try {
      await api(`/api/v1/automations/${initial.id}/enabled`, { method: 'POST', json: { enabled } });
      await qc.invalidateQueries({ queryKey: queryKeys.automation(initial.id) });
      await qc.invalidateQueries({ queryKey: queryKeys.automations() });
      toast(enabled ? 'Workflow enabled' : 'Workflow turned off');
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  }

  const problemNodes = new Set(problems.map((p) => p.nodeId).filter(Boolean) as string[]);
  const node = selected.length === 1 ? graph.nodes.find((n) => n.id === selected[0]) ?? null : null;
  const several = selected.length > 1 ? graph.nodes.filter((n) => selected.includes(n.id)) : [];
  const trigger = graph.nodes.find((n) => n.type === 'trigger');
  const saveLabel = { saved: 'Saved', pending: 'Unsaved changes', saving: 'Saving…', conflict: 'Conflict: reload', error: 'Not saved' }[save];

  return (
    <div className="builder">
      <header className="topbar">
        <Link to="/workflows/list" className="btn ghost icon" aria-label="Back to workflows"><ArrowLeft size={16} /></Link>
        <input className="title-input" value={name} aria-label="Workflow name" disabled={!canManage} onChange={(e) => { setName(e.target.value); latest.current.name = e.target.value; schedule(); }} data-testid="workflow-name" />
        <span className={`save-state ${save}`} data-testid="save-state" data-state={save}>{save === 'saving' ? <Loader2 size={12} className="spin" /> : save === 'saved' ? <Check size={12} /> : null} {saveLabel}</span>
        <div className="topbar-spacer" />
        {detail.currentVersion ? <span className="pill">v{detail.currentVersion}{draftChanged ? ' · draft has changes' : ''}</span> : <span className="pill">Not published</span>}
        {canManage && detail.currentVersion && (
          <button className={`btn ${detail.enabled ? '' : 'ghost'}`} onClick={() => void setEnabled(!detail.enabled)} data-testid="toggle-enabled" title={detail.enabled ? 'Turn off: triggers stop starting runs' : 'Enable the published version'}>
            <Power size={14} color={detail.enabled ? 'var(--ready)' : undefined} /> {detail.enabled ? 'Enabled' : 'Off'}
          </button>
        )}
        {canManage && trigger?.type === 'trigger' && trigger.trigger.type === 'manual' && (
          <RunAccessButton id={initial.id} access={detail.runAccess} restricted={detail.restricted} />
        )}
        {canManage && trigger?.type === 'trigger' && trigger.trigger.type === 'webhook' && <button className="btn ghost" onClick={() => setModal('token')}><KeyRound size={14} /> Webhook token</button>}
        {canManage && detail.currentVersion && <button className="btn" onClick={() => setModal('run')} data-testid="run-now"><Play size={14} /> Run now</button>}
        {canManage && <button className="btn ghost danger icon" aria-label="Delete workflow" title="Delete workflow" onClick={() => setModal('delete')} data-testid="delete-workflow"><Trash2 size={14} /></button>}
        {canManage && <button className="btn primary" disabled={problems.length > 0 || save === 'conflict'} title={problems.length ? 'Fix the problems first' : undefined} onClick={async () => { if (await flush()) setModal('publish'); }} data-testid="publish"><Rocket size={14} /> Publish</button>}
      </header>
      <div className="builder-body">
        {canManage && (
          <aside className="palette" aria-label="Steps">
            <p className="section-label">Add a step</p>
            {PALETTE.map((t) => (
              <button key={t} className={`palette-item cat-${NODE_TYPES[t].category}`} onClick={() => addNode(t)} title={NODE_TYPES[t].description} data-testid={`add-${t}`}>
                {nodeIcon(t)}<span>{NODE_TYPES[t].label}</span>
              </button>
            ))}
            {/* 1.3.1 (Q4, portal-steps.tsx): Assign for delivery and Wait for delivery. */}
            <PortalPaletteItems onAdd={addNode} />
            <p className="help" style={{ marginTop: 12 }}>A new step connects after the selected one. Drag from a port to connect; select a line or step and press Delete to remove it. Shift-click or drag a box to select several; ⌘A selects all.</p>
          </aside>
        )}
        {/* 1.9.3: the inspector is resizable (remembered per browser); the palette stays 200 px. */}
        <Group orientation="horizontal" className="panel-group builder-panels" defaultLayout={layout.defaultLayout} onLayoutChanged={layout.onLayoutChanged}>
        <Panel id="flow-canvas" minSize="320px" className="builder-pane" style={PANE}>
        <GraphCanvas graph={graph} onChange={canManage ? change : undefined} selected={selected} onSelect={setSelected} onDeleteSteps={deleteSteps} problems={problemNodes} readOnly={!canManage} />
        </Panel>
        <Separator className="resize-handle" id="builder-inspector-resize" />
        <Panel id="flow-inspector" defaultSize="360px" minSize="300px" maxSize="60%" className="builder-pane" style={PANE}>
        <aside className="builder-inspector" data-testid="builder-inspector">
          {several.length > 1 ? (
            <div className="inspector-pane" data-testid="steps-selected">
              <p className="section-label" style={{ margin: 0 }}>{several.length} steps selected</p>
              <span className="help">Drag one of them to move them all; Esc clears.{several.some((n) => n.type === 'trigger') ? ' The trigger stays when the others are deleted.' : ''}</span>
              {canManage && removable(graph, selected).length > 0 && (
                <div><button className="btn danger sm" onClick={() => deleteSteps(selected)} data-testid="delete-steps"><Trash2 size={13} /> Delete steps</button></div>
              )}
            </div>
          ) : node ? (
            <NodeInspector key={node.id} node={node} graph={graph} disabled={!canManage} definitionId={initial.id}
              problems={problems.filter((p) => p.nodeId === node.id).map((p) => p.message)}
              onPatch={(n) => change({ ...graph, nodes: graph.nodes.map((x) => (x.id === n.id ? n : x)) })}
              onRemove={() => { change(removeNodes(graph, [node.id])); setSelected([]); }} />
          ) : (
            <div className="inspector-pane">
              <p className="section-label" style={{ margin: 0 }}>Workflow</p>
              <div className="field"><label htmlFor="wf-desc">Description</label><textarea id="wf-desc" className="textarea" rows={2} value={description} disabled={!canManage} onChange={(e) => { setDescription(e.target.value); latest.current.description = e.target.value; schedule(); }} /></div>
              <div className="field"><label htmlFor="wf-max">Concurrent runs</label><input id="wf-max" className="input" type="number" min={1} max={100} value={maxRuns} disabled={!canManage} onChange={(e) => { const v = Math.max(1, Math.min(100, Number(e.target.value) || 1)); setMaxRuns(v); latest.current.maxRuns = v; schedule(); }} /><span className="help">Runs beyond this wait for capacity instead of overloading workers.</span></div>
              <p className="section-label">{problems.length ? `Problems (${problems.length})` : 'Ready to publish'}</p>
              {problems.length === 0 ? <span className="muted"><Check size={13} color="var(--ready)" /> The draft is valid.</span> : (
                <ul className="problem-list" data-testid="problems">
                  {problems.map((p, i) => <li key={i}><button className="link" onClick={() => p.nodeId && setSelected([p.nodeId])}><AlertTriangle size={12} color="var(--warning)" /> {p.message}</button></li>)}
                </ul>
              )}
              <p className="section-label">Versions</p>
              {detail.versions.length === 0 ? <span className="muted">Nothing published yet.</span> : (
                <ul className="version-list">
                  {detail.versions.map((v) => (
                    <li key={v.version}><span className="mono">v{v.version}</span> <span className="muted">{relative(v.publishedAt)}{v.publishedBy ? ` · ${v.publishedBy}` : ''}</span>{v.note && <div className="faint">{v.note}</div>}</li>
                  ))}
                </ul>
              )}
              <p className="section-label">Recent runs</p>
              <RunList definitionId={initial.id} compact />
            </div>
          )}
        </aside>
        </Panel>
        </Group>
      </div>
      {confirmDelete && (
        <Modal title={`Delete ${confirmDelete.length} steps?`} onClose={() => setConfirmDelete(null)} actions={<>
          <button className="btn ghost" onClick={() => setConfirmDelete(null)}>Cancel</button>
          <button className="btn danger" onClick={() => removeSteps(confirmDelete)} data-testid="confirm-delete-steps"><Trash2 size={14} /> Delete steps</button>
        </>}>
          <p className="muted" style={{ marginTop: 0 }}>The steps and their lines leave the draft. The flow editor has no undo; the published version is not changed until you publish again.</p>
        </Modal>
      )}
      {modal === 'publish' && <PublishModal id={initial.id} revision={revision.current} version={(detail.currentVersion ?? 0) + 1} enabled={detail.currentVersion ? detail.enabled : true} onClose={() => setModal(null)} onDone={() => { setDraftChanged(false); setModal(null); }} />}
      {modal === 'run' && <RunModal id={initial.id} subject={runSubject(graph)} onClose={() => setModal(null)} onStarted={(runId) => navigate(`/workflows/runs/${runId}`)} />}
      {modal === 'delete' && <DeleteWorkflowModal id={initial.id} name={name} onClose={() => setModal(null)} onDeleted={() => navigate('/workflows/list')} />}
      {modal === 'token' && <TokenModal id={initial.id} url={detail.webhookUrl} has={detail.hasWebhookToken} onClose={() => setModal(null)} />}
    </div>
  );
}

/** Deleting warns first and needs the workflow's name typed. */
function DeleteWorkflowModal({ id, name, onClose, onDeleted }: { id: string; name: string; onClose: () => void; onDeleted: () => void }) {
  const qc = useQueryClient();
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  async function remove() {
    setBusy(true);
    try {
      const r = await api<{ activeRuns: number }>(`/api/v1/automations/${id}`, { method: 'DELETE' });
      await qc.invalidateQueries({ queryKey: queryKeys.automations() });
      toast(`Deleted ${name}${r.activeRuns ? `; ${r.activeRuns} run${r.activeRuns === 1 ? '' : 's'} under way will finish` : ''}`);
      onDeleted();
    } catch (err) {
      toast(errorMessage(err), 'error');
      setBusy(false);
    }
  }
  return (
    <Modal title={`Delete ${name}?`} onClose={onClose} actions={<>
      <button className="btn ghost" onClick={onClose}>Cancel</button>
      <button className="btn danger" disabled={typed !== name || busy} onClick={() => void remove()} data-testid="confirm-delete-workflow"><Trash2 size={14} /> Delete workflow</button>
    </>}>
      <div className="banner warning"><div className="banner-body">
        <strong>The workflow stops and leaves the list</strong>
        <span>Its triggers, schedule and webhook token stop working. Runs already under way finish, and past runs stay visible in Monitor and on assets.</span>
      </div></div>
      <div className="field"><label htmlFor="confirm-wf">Type <strong>{name}</strong> to confirm</label>
        <input id="confirm-wf" className="input" value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" data-testid="confirm-workflow-name" /></div>
    </Modal>
  );
}

function PublishModal({ id, revision, version, enabled, onClose, onDone }: { id: string; revision: number; version: number; enabled: boolean; onClose: () => void; onDone: () => void }) {
  const qc = useQueryClient();
  const [note, setNote] = useState('');
  const [enable, setEnable] = useState(enabled);
  const [busy, setBusy] = useState(false);
  async function publish() {
    setBusy(true);
    try {
      await api(`/api/v1/automations/${id}/publish`, { method: 'POST', json: { draftRevision: revision, note: note || undefined, enable } });
      await qc.invalidateQueries({ queryKey: queryKeys.automation(id) });
      await qc.invalidateQueries({ queryKey: queryKeys.automations() });
      toast(`Published version ${version}`);
      onDone();
    } catch (err) {
      toast(errorMessage(err), 'error');
      setBusy(false);
    }
  }
  return (
    <Modal title={`Publish version ${version}`} onClose={onClose} actions={<><button className="btn ghost" onClick={onClose}>Cancel</button><button className="btn primary" disabled={busy} onClick={() => void publish()} data-testid="confirm-publish"><Rocket size={14} /> Publish</button></>}>
      <p className="muted" style={{ marginTop: 0 }}>New runs use this version. Runs already in progress finish on the version they started with.</p>
      <div className="field"><label htmlFor="pub-note">What changed</label><input id="pub-note" className="input" autoFocus value={note} placeholder="Optional note" onChange={(e) => setNote(e.target.value)} /></div>
      <label className="option-row"><input type="checkbox" checked={enable} onChange={(e) => setEnable(e.target.checked)} data-testid="publish-enable" /> Enable triggers after publishing</label>
    </Modal>
  );
}

function RunModal({ id, subject, onClose, onStarted }: { id: string; subject: ReturnType<typeof runSubject>; onClose: () => void; onStarted: (runId: string) => void }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState<LibraryResult['items']>([]);
  const sequences = useSequences().data?.items ?? [];
  const [kind, setKind] = useState<'asset' | 'sequence'>(subject.asset === 'none' || subject.sequence === 'required' ? 'sequence' : 'asset');
  const [assetId, setAssetId] = useState<string | null>(null);
  const [sequenceId, setSequenceId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const t = window.setTimeout(() => {
      const p = new URLSearchParams({ sort: 'newest' });
      if (q) p.set('q', q);
      void api<LibraryResult>(`/api/v1/assets?${p}`).then((r) => setResults(r.items.slice(0, 8))).catch(() => setResults([]));
    }, 200);
    return () => window.clearTimeout(t);
  }, [q]);
  const chosen = kind === 'asset' ? assetId : sequenceId;
  const required = subject[kind] === 'required';
  // What the steps need of the chosen asset or sequence (preflight), checked as soon as it is chosen.
  const preflight = usePreflight(chosen ? { kind: 'workflow', workflowId: id, ...(kind === 'asset' ? { assetId: chosen } : { sequenceId: chosen }) } : null);
  const rights = useRightsCheck(chosen ? preflight.data?.problems : undefined);
  async function run(restore = false) {
    setBusy(true);
    try {
      const json = { ...(kind === 'asset' ? (assetId ? { assetId } : {}) : (sequenceId ? { sequenceId } : {})), ...(restore ? { restore: true } : {}), ...rights.field() };
      const r = await api<{ runId: string }>(`/api/v1/automations/${id}/runs`, { method: 'POST', json });
      toast('Run started');
      onStarted(r.runId);
    } catch (err) {
      toast(rights.takeError(err) ? 'Check the rights first' : errorMessage(err), 'error');
      setBusy(false);
    }
  }
  const choosable = (['asset', 'sequence'] as const).filter((k) => subject[k] !== 'none');
  return (
    <Modal title="Run now" onClose={onClose} actions={<><button className="btn ghost" onClick={onClose}>Cancel</button>{chosen && preflightRestorable(preflight) && <button className="btn" disabled={busy || !rights.ready} onClick={() => void run(true)} data-testid="confirm-restore-run"><Play size={14} /> Restore and start</button>}<button className="btn primary" disabled={busy || (required && !chosen) || (Boolean(chosen) && preflightBlocks(preflight)) || !rights.ready} onClick={() => void run()} data-testid="confirm-run"><Play size={14} /> Start run</button></>}>
      {choosable.length > 1 && (
        <div className="segmented" role="radiogroup" aria-label="Run on">
          <button aria-pressed={kind === 'asset'} onClick={() => setKind('asset')}>On an asset</button>
          <button aria-pressed={kind === 'sequence'} onClick={() => setKind('sequence')}>On a sequence</button>
        </div>
      )}
      <p className="muted" style={{ marginTop: 0 }}>{required ? `This workflow acts on ${kind === 'asset' ? 'an asset' : 'a sequence'}. Choose one.` : `Optionally run it on ${kind === 'asset' ? 'an asset' : 'a sequence'}.`}</p>
      {kind === 'asset' ? (
        <>
          <div className="search"><Search size={14} /><input className="input" autoFocus placeholder="Search the library" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search assets" /></div>
          <div className="asset-pick" role="listbox" aria-label="Assets">
            {results.map((a) => (
              <button key={a.id} role="option" aria-selected={assetId === a.id} className={`asset-pick-item ${assetId === a.id ? 'selected' : ''}`} onClick={() => setAssetId(assetId === a.id ? null : a.id)}>
                <span>{a.title}</span><span className="faint">{a.status}{a.mediaKind ? ` · ${a.mediaKind}` : ''}</span>
              </button>
            ))}
            {results.length === 0 && <span className="muted">No assets found.</span>}
          </div>
        </>
      ) : (
        <div className="asset-pick" role="listbox" aria-label="Sequences">
          {sequences.map((s) => (
            <button key={s.id} role="option" aria-selected={sequenceId === s.id} className={`asset-pick-item ${sequenceId === s.id ? 'selected' : ''}`} onClick={() => setSequenceId(sequenceId === s.id ? null : s.id)}>
              <span>{s.name}</span><span className="faint">{s.clips} clips</span>
            </button>
          ))}
          {sequences.length === 0 && <span className="muted">No sequences yet.</span>}
        </div>
      )}
      {chosen && <PreflightProblems q={preflight} what="This run" />}
      {chosen && <RightsCheckBlock check={rights} />}

    </Modal>
  );
}

function TokenModal({ id, url, has, onClose }: { id: string; url: string; has: boolean; onClose: () => void }) {
  const [token, setToken] = useState<string | null>(null);
  const full = `${window.location.origin}${url}`;
  async function rotate() {
    try {
      setToken((await api<{ token: string }>(`/api/v1/automations/${id}/webhook-token`, { method: 'POST' })).token);
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  }
  return (
    <Modal title="Webhook trigger" onClose={onClose} actions={<button className="btn" onClick={onClose}>Done</button>}>
      <div className="field"><label>Endpoint</label><code className="code-block">POST {full}</code></div>
      {token ? (
        <div className="field"><label>Token (shown once)</label><code className="code-block" data-testid="webhook-token">{token}</code>
          <span className="help">Send it as <code>Authorization: Bearer …</code>. The previous token no longer works.</span></div>
      ) : (
        <>
          <p className="muted">{has ? 'A token exists. Generating a new one revokes it.' : 'No token yet; the endpoint rejects every call until you create one.'}</p>
          <button className="btn primary" onClick={() => void rotate()}><KeyRound size={14} /> {has ? 'Rotate token' : 'Create token'}</button>
        </>
      )}
    </Modal>
  );
}
